import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { debounceTime, filter, Subscription, tap } from 'rxjs';
import { LoaderService } from '@/common/loader/loader.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { StopKind } from '@/common/enums/BagTransport';
import { BagTransportTermApi } from '@/bag/bag-transport-term/bag-transport-term-modal-transport/bag-transport-term.api.service';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import {
	Transport,
	TransportKind,
	TransportModule,
	TransportSide,
	TransportSideType,
	TransportTerm,
} from '@/new-transport/_models/transport.model';
import { TransportLogic } from '@/new-transport/transport-map/transport-map.api.service';
import { TransportTimetable } from '@/new-transport/_models/transport-timetable.model';

interface DialogData {
	side: TransportSideType;
	elements: TransportTerm[];
	same: boolean;
	transport: number;
	terms: number;
	exist: boolean;
	transportKind: OfferTransportKind;
	linkedTransports: number[];
}

@Component({
	selector: 'app-bag-transport-term-modal-transport',
	templateUrl: './bag-transport-term-modal-transport.component.html',
	styleUrls: ['./bag-transport-term-modal-transport.component.scss'],
})
export class BagTransportTermModalTransportComponent implements OnInit, OnDestroy {
	constructor(
		@Inject(MAT_DIALOG_DATA) public data: DialogData,
		private readonly dialogRef: MatDialogRef<BagTransportTermModalTransportComponent>,
		private readonly loader: LoaderService,
		private readonly infobox: InfoboxService,
		private readonly bagTransportTermApi: BagTransportTermApi,
	) {}

	selectedTemplate: FormControl = new FormControl<Transport>({
		departureTerms: [],
		disabled: false,
		id: 0,
		name: '',
		returnTerms: [],
		side: '' as TransportSide,
		value: undefined,
	});

	linkedTransport = false;
	linkedTransportMessage = '';

	selectedOnlyLinkedTransports = false;

	templateDestination = null;

	readonly TransportModule = TransportModule;
	readonly OfferTransportKind = OfferTransportKind;

	protected templates: Transport[] = [];
	private templateSubscription!: Subscription;

	private areLinkedTransportTheSame = false;

	async ngOnInit(): Promise<void> {
		this.checkIfTransportIsLinked();
		await this.loadDestinationsStops();
	}

	ngOnDestroy(): void {
		this.templateSubscription?.unsubscribe();
	}

	displayFn(item: Transport): string {
		return item.name ? `${item.name} - ${item.side}` : '';
	}

	async goToTransport(kind: TransportModule): Promise<void> {
		let transports = '';
		try {
			transports = await this.resolveTransports();
		} catch {
			return;
		}

		const baseUrl = this.buildBaseUrl(kind, transports);
		const finalUrl = baseUrl.includes('?')
			? baseUrl.endsWith('?') || baseUrl.endsWith('&')
				? `${baseUrl}transportKind=${this.data.transportKind}`
				: `${baseUrl}&transportKind=${this.data.transportKind}`
			: `${baseUrl}?transportKind=${this.data.transportKind}`;

		window.open(finalUrl, '_blank');
	}

	/**
	 * The Process of loading template into existing transport term consists of the following steps:
	 * - Checking if the term and template sides are compatible (you should not be able to load return template into departure term)
	 * - Fetching all logics and timetables from template
	 * - Creating new transport,
	 * - Creating new logics and timetables based of chosen template
	 * - Keeping the destination as it was from the transport term
	 * - Redirecting user to timetable view
	 */
	async loadTemplate(): Promise<void> {
		if (!this.selectedTemplate.value.id) {
			this.infobox.error('Wybierz szablon');
			return;
		}

		const templateSide = this.selectedTemplate.value.side;
		const mapping = {
			D: 'Wyjazd',
			R: 'Powrót',
		};

		if (this.data.side !== templateSide) {
			this.infobox.error(
				`Nie zgodne typy wyjazdów! Nie można wgrać szablonu o typie: ${
					mapping[templateSide]
				} do transportu o typie: ${mapping[this.data.side]}`,
			);
			return;
		}

		const templateId: number = this.selectedTemplate.value.id;

		this.loader.show();
		const templateLogics = await this.bagTransportTermApi.fetchLogicsFromTransport(templateId);
		const templateTimetables = await this.bagTransportTermApi.fetchTimetablesFromTransport(templateId);

		const newTransport = await this.bagTransportTermApi.createNewTransport(this.data.elements, this.data.side);
		this.loader.hide();

		if (templateLogics && templateTimetables) {
			this.loader.show();
			await this.fillTransportData(templateLogics, templateTimetables, newTransport);
			this.loader.hide();

			this.infobox.success('Zakończono wgrywanie szablonu!');
			this.dialogRef.close(true);
			window.open(`/transport/${newTransport.id}/table/new`);
		} else {
			this.infobox.error('Wystąpił błąd przy pobieraniu przystanków dla wybranego szablonu');
		}
	}

	private buildBaseUrl(kind: TransportModule, transports: string): string {
		switch (kind) {
			case TransportModule.MAP:
				if (!this.data.same && !this.selectedOnlyLinkedTransports) {
					return `transport/map/new?side=${this.data.side}&terms=${this.data.terms}&kind=${TransportKind.OFFER}&${transports}`;
				}
				return `transport/${this.data.transport}/map/new?${transports}`;

			case TransportModule.COACH_LIST:
				return `transport/${this.data.transport}/coach?${transports}`;

			case TransportModule.TIMETABLE:
				return `transport/${this.data.transport}/table/new?${transports}`;

			case TransportModule.LIST:
				return `transport/${this.data.transport}/list?${transports}`;

			default:
				const exhaustiveCheck: never = kind;
				throw new Error(`Unhandled TransportModule: ${exhaustiveCheck}`);
		}
	}

	private async resolveTransports(): Promise<string> {
		if (
			(this.data.elements.length === 1 && this.data.linkedTransports.length) ||
			this.selectedOnlyLinkedTransports
		) {
			try {
				const baseTransports = await this.bagTransportTermApi.getBaseTransports(this.data.linkedTransports[0]);
				return `transports=${baseTransports.map((v) => v.id).join(',')}`;
			} catch (e) {
				this.infobox.error(`Wystąpił błąd przy pobieraniu bazowych transportów: ${JSON.stringify(e)}`);
				throw e;
			}
		}

		if (!this.data.same && !this.selectedOnlyLinkedTransports) {
			const property = this.data.side === TransportSide.DEPARTURE ? 'transportDeparture' : 'transportReturn';

			if (this.data.elements.some((v) => !v[property]?.id)) return '';

			return `transports=${this.data.elements.map((v) => v[property]?.id).join(',')}`;
		}

		return '';
	}

	private checkIfTransportIsLinked(): void {
		this.areLinkedTransportTheSame = this.data.linkedTransports?.every((v) => v === this.data.linkedTransports[0]);
		this.selectedOnlyLinkedTransports =
			this.data.elements?.length === this.data.linkedTransports?.length && this.areLinkedTransportTheSame;

		if (!this.data.linkedTransports?.length) return;

		this.linkedTransport = true;

		if (
			(this.data.elements.length === 1 && this.data.linkedTransports.length) ||
			this.selectedOnlyLinkedTransports
		) {
			this.data.transport = this.data.linkedTransports[0];
			this.linkedTransportMessage =
				'Ten transport jest już połączony, zostaniesz przekierowany do transportu wynikowego.';
		} else if (this.data.elements.length > 1) {
			this.linkedTransportMessage =
				'Co najmniej jeden z transportów jest już połączony, miej to na uwadze podczas łączenia transportów';
		}
	}

	private async loadDestinationsStops(): Promise<void> {
		if (!this.data.exist) {
			this.loader.show();
			const destinations = await this.bagTransportTermApi.getTemplateDestinations();
			if (destinations.length) {
				this.templateDestination = destinations[0];
			} else {
				this.infobox.error('Nie udało się załadować destynacji dla tego szablonu!');
			}
			this.templates = await this.bagTransportTermApi.fetchTemplates();
			this.loader.hide();
			this.initializeTemplateSelectSubscription();
		}
	}

	private initializeTemplateSelectSubscription(): void {
		const DEBOUNCE_TIME = 1000;
		this.templateSubscription = this.selectedTemplate.valueChanges
			.pipe(
				debounceTime(DEBOUNCE_TIME),
				filter((searchTerm: string) => searchTerm.length > 2),
				tap(async (searchTerm) => {
					this.loader.show();
					const templates: Transport[] = await this.bagTransportTermApi.searchTemplates(searchTerm);
					if (templates) {
						this.templates = templates;
					}
					this.loader.hide();
				}),
			)
			.subscribe();
	}

	private async fillTransportData(
		logics: TransportLogic[],
		timetables: TransportTimetable[],
		transport: Transport,
	): Promise<void> {
		return new Promise(async (resolve) => {
			const term: TransportTerm = this.data.elements[0];
			const requests: Promise<void>[] = [];
			const logicPayload: TransportLogic[] = logics.map((logic) => ({
				...logic,
				transport: transport.id,
				stopDestination: term.transportStop.id,
				originCity: term.transportStop.id,
				stopDestinationName: term.name,
				originCityName: term.name,
				city: logic.action === StopKind.TARGET ? term.transportStop.id : logic.city,
				vehicle: logic.vehicle['id'],
			}));

			const timetablePayload = timetables.map((timetable) => {
				return {
					...timetable,
					transport: transport.id,
					timeDeparture:
						timetable.stop.id === this.templateDestination.id
							? `${
									this.data.side === TransportSide.DEPARTURE
										? term.accommodation['lower']
										: term.accommodation['upper']
								}T10:00`
							: timetable.timeDeparture,
					stop: timetable.stop.id === this.templateDestination.id ? term.transportStop.id : timetable.stop.id,
				};
			});

			requests.push(
				this.bagTransportTermApi.createResource('/transport/transport_logic/', logicPayload),
				this.bagTransportTermApi.createResource('/transport/timetable/', timetablePayload),
			);

			await Promise.all(requests).catch((err) => {
				this.infobox.error(`Wystąpił problem przy kopiowaniu przystanków: ${err}`);
			});

			resolve();
		});
	}
}
